/**
 * What this machine holds that the server cannot send back.
 *
 * `schema.ts` says it in its header: from version 2 on, `registerEntries` is no
 * longer a cache. It and `mutations` are the only copy of anything written since
 * the last successful push, and the same became true of `conflicts` at version
 * 4 and of `notificationActions` at version 7. §22 turns that observation into a
 * rule — a migration that cannot preserve pending work must refuse to run — and
 * a rule needs something countable to be checked against.
 *
 * So this is the census: for each store, how many rows in it represent work that
 * would simply be gone. Not the row count. A register holding four thousand
 * synchronised entries has nothing at stake in any of them; the eleven with
 * `unsynced` set are the whole question. Counting everything would make the
 * guard fire on an upgrade that legitimately evicts old cache, and a guard that
 * fires on ordinary work is a guard somebody turns off.
 *
 * Read from a Dexie instance *or* from a version-change transaction, because it
 * is asked in both places — once around each upgrade callback, where a shortfall
 * can still be rolled back, and once before the database is opened at all, where
 * a store about to be dropped can still be refused.
 */
import type Dexie from "dexie"
import type { Transaction } from "dexie"

/**
 * A source that can be counted: an open database, or the transaction an upgrade
 * runs inside. Dexie's `Table` API is the same through either, and the only
 * thing that differs is how the set of available tables is spelled.
 */
export type CensusSource = Dexie | Transaction

/** One entry per store that can hold irreplaceable work. */
export interface DurableStore {
  store: string
  /** For the refusal message, which an operator reads at three in the morning. */
  label: string
  count: (table: CensusTable) => Promise<number>
}

/** The slice of Dexie's `Table` this file uses, so either source satisfies it. */
export interface CensusTable {
  schema: { indexes: Array<{ name: string }> }
  count: () => Promise<number>
  where: (index: string) => { anyOf: (values: unknown[]) => { count: () => Promise<number> } }
  filter: (predicate: (record: never) => boolean) => { count: () => Promise<number> }
}

type CensusRecord = Record<string, unknown>

/**
 * Count by index where there is one, and by scan where there is not.
 *
 * The census runs *inside* an upgrade, which means it runs against whichever
 * intermediate schema the version being replayed happened to declare — and the
 * columns it asks about were added over five versions. Version 2's
 * `registerEntries` has no `rejected` index, so the query that is right
 * everywhere else raises `SchemaError` there and takes the whole upgrade down
 * with it: a guard that aborts good migrations is worse than no guard.
 *
 * The fallback scan is affordable precisely where it is needed. An upgrade is
 * already rewriting every row of the store it touches, and the pre-flight census
 * runs once per application start.
 */
async function countWhere(
  table: CensusTable,
  index: string,
  values: unknown[],
  matches: (record: CensusRecord) => boolean
): Promise<number> {
  if (table.schema.indexes.some(spec => spec.name === index)) {
    return table.where(index).anyOf(values).count()
  }

  return table.filter(matches as unknown as (record: never) => boolean).count()
}

/**
 * The states a mutation can be in and still be owed something.
 *
 * `rejected` is in the list and that is deliberate, for the reason
 * `StoredRegisterEntry#rejected` is its own column: a refusal is terminal for
 * the *server*, and entirely unfinished for the operator, who has not yet been
 * told what to re-enter. Dropping those in a migration would delete the only
 * record of what was refused.
 */
export const UNSETTLED_MUTATION_STATES = [ "pending", "syncing", "conflict", "rejected" ]

export const DURABLE_STORES: readonly DurableStore[] = [
  {
    store: "mutations",
    label: "unsent changes",
    count: table =>
      countWhere(table, "state", UNSETTLED_MUTATION_STATES, record =>
        UNSETTLED_MUTATION_STATES.includes(record.state as string))
  },
  {
    store: "registerEntries",
    label: "entries not yet accepted by the server",
    // Three queries rather than one, because a row can be any combination of the
    // flags and Dexie has no OR across separate indexes. The alternative is a
    // full scan of the largest store in the database on every open, which is the
    // one thing this must not cost.
    count: async table => {
      const unsynced = await countWhere(table, "unsynced", [ 1 ], record => record.unsynced === 1)
      const rejected = await countWhere(table, "rejected", [ 1 ], record => record.rejected === 1)
      const conflicted = await countWhere(table, "conflicted", [ 1 ], record => record.conflicted === 1)

      // A sum rather than a set union: the same row can be counted twice, which
      // overstates the census. That is the safe direction — the guard compares a
      // number before against the same number after, and both are overstated
      // identically, so an equal count still means nothing was lost.
      return unsynced + rejected + conflicted
    }
  },
  {
    store: "conflicts",
    label: "conflicts awaiting a decision",
    count: table => countWhere(table, "resolved", [ 0 ], record => record.resolved === 0)
  },
  {
    store: "entryBaseSnapshots",
    label: "the values a conflict would be judged against",
    count: table => table.count()
  },
  {
    store: "provisionalSequences",
    label: "offline sequence numbers",
    count: table => table.count()
  },
  {
    store: "notificationActions",
    label: "notification reads and dismissals owed to the server",
    count: table =>
      countWhere(table, "state", UNSETTLED_MUTATION_STATES, record =>
        UNSETTLED_MUTATION_STATES.includes(record.state as string))
  },
  {
    store: "localNotifications",
    label: "notices raised during the outage",
    count: table => table.count()
  },
  {
    store: "auditLog",
    label: "offline sign-ins not yet reported",
    // Indexed on `syncedAt` since version 1 precisely so this question is a
    // query. `equals(null)` will not do it — IndexedDB does not index null — so
    // this is the one census that reads rows.
    count: table => table.filter((record: { syncedAt: string | null }) => record.syncedAt === null).count()
  },
  {
    store: "outboxState",
    label: "this computer's outbox counter",
    // Not work, but the thing that makes work orderable. Losing the row resets
    // `deviceSeq` to zero, and a second mutation numbered 4 is a causal order
    // that says two different things.
    count: table => table.count()
  }
]

/** Store name to the number of at-risk rows in it. Absent stores are absent. */
export type DurableCensus = Record<string, number>

export function censusTotal(census: DurableCensus): number {
  return Object.values(census).reduce((total, count) => total + count, 0)
}

/**
 * Which tables the source actually has, since older versions have fewer.
 *
 * A transaction is asked for its `schema` rather than its `storeNames`, and the
 * difference is not cosmetic. Dexie replays every intermediate version inside
 * one version-change transaction whose `storeNames` covers the *final* schema,
 * while `Transaction#table` refuses anything absent from the schema of the
 * version currently being replayed. Reading the wider of the two makes the
 * census ask version 4's upgrade for a store version 7 introduces, and Dexie
 * answers that by aborting the whole upgrade.
 */
export function tablesIn(source: CensusSource): Set<string> {
  const database = source as Dexie

  if (Array.isArray(database.tables)) return new Set(database.tables.map(table => table.name))

  // `schema` is on every Dexie transaction and absent from its published types.
  return new Set(Object.keys((source as Transaction & { schema?: Record<string, unknown> }).schema ?? {}))
}

/**
 * Count the work at risk, skipping stores this source does not have.
 *
 * A missing store is not a zero. Version 1 had no `mutations` table at all, and
 * recording it as zero would make the guard compare "no such store" against "no
 * rows" and find them equal — which they are, and which is also exactly what a
 * store being dropped looks like. Omitting the key keeps the two distinguishable
 * for `shortfallsBetween`.
 */
export async function countDurableWork(source: CensusSource): Promise<DurableCensus> {
  const available = tablesIn(source)
  const census: DurableCensus = {}
  // `Dexie#table` and `Transaction#table` have the same runtime contract and
  // overload sets TypeScript will not reconcile across the union.
  const tableOf = (store: string) => (source as { table: (name: string) => unknown }).table(store) as CensusTable

  for (const durable of DURABLE_STORES) {
    if (!available.has(durable.store)) continue

    census[durable.store] = await durable.count(tableOf(durable.store))
  }

  return census
}

export interface Shortfall {
  store: string
  label: string
  before: number
  after: number
}

/**
 * What the second census lost relative to the first.
 *
 * A store that was present and is now absent counts its whole population as
 * lost, which is the case a row-by-row comparison cannot see: dropping the
 * table drops every row in it and leaves nothing behind to count.
 */
export function shortfallsBetween(before: DurableCensus, after: DurableCensus): Shortfall[] {
  return DURABLE_STORES.flatMap(durable => {
    const had = before[durable.store]
    if (had === undefined || had === 0) return []

    const has = after[durable.store] ?? 0
    if (has >= had) return []

    return [ { store: durable.store, label: durable.label, before: had, after: has } ]
  })
}
